import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dialog } from "@headlessui/react";
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import {
  Button,
  IssueForm,
  NotificationPopup,
  ProductTourComponent,
  PromptModal,
  WarningModal,
} from "components";
import { WelcomeModal } from "components/WelcomeModal";
import GlobalHeader, { NavItem } from "./GlobalHeader";
import ContextualBar from "./ContextualBar";
import FiltersDrawer from "./FiltersDrawer";
import SlideMenuList from "./SlideMenuList";
import { SlideOver } from "components/SlideOver";

import {
  useActionGroupsHandouts,
  useCreateHandout,
  useDebouncedHandler,
  useFindUnreadActivitiesAmount,
  useIssueRatingCategories,
  useMe,
  useQueryOptionsSetter,
  useSkills,
  useUserSkills,
  useWindowDimensions,
} from "hooks";
import { useHandleJoyrideCallback } from "./MenuBar.hooks";
import { IAISuggestions, ISelectOption, IUser } from "types";
import {
  computeCenterAndRadius,
  getRating,
  logout,
  useGoogleMaps,
} from "utils";
import { getGeocode } from "use-places-autocomplete";
import { isNumber } from "lodash";
import { useSetState } from "react-use";
import Joyride, { Step } from "react-joyride";
import { useQueryClient } from "react-query";

import { firstScenarioSteps, fullProductTour } from "const/tourSteps";
import { APP_ROUTES } from "const";

interface State {
  run: boolean;
  stepIndex: number;
  steps: Step[];
}

type PageContextKey = "issues" | "solutions" | "action-groups" | "handouts";

interface ContextualConfig {
  key: PageContextKey;
  showSearch: boolean;
  showFilters: boolean;
  ctaLabel: string;
  ctaDisabled?: boolean;
  onCta: () => void;
  rightExtras?: React.ReactNode;
}

interface IMenu {
  isAuth?: boolean;
  setPdfUrl?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setHandoutsNoGroupsFound?: React.Dispatch<
    React.SetStateAction<"NoGroups" | "Groups" | "NoSearch">
  >;
  primaryNavigation: NavItem[];
  moreNavigation: NavItem[];
  profile?: IUser;
  isFetching: boolean;
  isScrolled: boolean;
}

const random = Math.round(Math.random() * 10);
const avatarColor =
  random < 4
    ? "lightCoral"
    : random > 4 && random < 7
    ? "lightGreen"
    : "mediumPurple";

const initialCoordinates = {
  lat: "",
  lng: "",
  type: "",
  viewport: null as google.maps.LatLngBounds | null,
};

function DesktopMenu({
  isAuth,
  setPdfUrl,
  setHandoutsNoGroupsFound,
  primaryNavigation,
  moreNavigation,
  profile,
  isFetching,
}: IMenu) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const setQueryOptions = useQueryOptionsSetter(searchParams, setSearchParams);
  const { data: meData } = useMe();
  const { data: userSkillsData } = useUserSkills({ id: meData?.data.id });

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showGoSignInModal, setShowGoSignInModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [openProductTourPopup, setOpenProductTourPopup] = useState(false);
  const [openNotifications, setOpenNotifications] = useState(false);
  const [openNotificationSlideover, setOpenNotificationSlideover] =
    useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [preventClose, setPreventClose] = useState(false);
  const [openIssueModal, setOpenIssueModal] = useState(false);
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [showReportPromptModal, setShowReportPromptModal] = useState(false);
  const [createdIssueId, setCreatedIssueId] = useState<string | undefined>();
  const [showSuccessReportPromptModal, setShowSuccessReportPromptModal] =
    useState(false);

  const { data: unreadActivitiesAmount } = useFindUnreadActivitiesAmount({
    enabled: !!isAuth,
  });
  const dimensions = useWindowDimensions();

  const values = useMemo(() => {
    return {
      category: searchParams.getAll("category"),
      skills: searchParams.getAll("skills"),
      user: searchParams.get("user") || "",
    };
  }, [searchParams]);

  const [skillSearchParam, setSkillSearchParam] = useState("");
  const skills = useSkills({
    search: skillSearchParam,
    enabled:
      location.pathname === APP_ROUTES.SOLUTION_LIST ||
      location.pathname === APP_ROUTES.ACTION_GROUP_LIST,
  });

  const skillOptions = useMemo<ISelectOption[]>(() => {
    return (
      skills?.data?.data.map((skill) => ({
        value: skill.id!,
        label: skill.name,
      })) || []
    );
  }, [skills?.data?.data]);

  const skillValue = useMemo<ISelectOption[]>(() => {
    return values.skills.map((item) => ({
      value: item || "",
      label:
        skillOptions.find((option) => option.value === item)?.label || "",
    }));
  }, [skillOptions, values.skills]);

  const [currentSelectedSkills, setCurrentSelectedSkills] =
    useState<ISelectOption[]>(skillValue);

  useEffect(() => {
    if (values.skills.length < 1) {
      setCurrentSelectedSkills([]);
    }
  }, [values.skills]);

  useEffect(() => {
    setCurrentSelectedSkills(skillValue);
  }, [skillValue]);

  const [userLocationCoordinates, setUserLocationCoordinates] = useState(
    initialCoordinates
  );
  const [searchLocationName, setSearchLocationName] = useState("");
  const placeId = searchParams.get("placeId") || "";
  const mapsLoaded = useGoogleMaps();

  useEffect(() => {
    if (!placeId) {
      setUserLocationCoordinates(initialCoordinates);
      setSearchLocationName("");
      return;
    }

    // getGeocode calls into the Maps SDK, which is now loaded on demand. Wait
    // for it rather than falling through and clearing the selected location.
    if (!mapsLoaded) return;

    getGeocode({ placeId }).then((result) => {
      const latlng = result[0].geometry.location;
      const lat = String(latlng?.lat());
      const lng = String(latlng?.lng());
      const type = result[0].types.includes("continent")
        ? "continent"
        : result[0].types.includes("locality")
        ? "locality"
        : "country";
      setSearchLocationName(result[0].formatted_address);
      setUserLocationCoordinates({
        lat,
        lng,
        type,
        viewport: result[0].geometry.viewport,
      });
    });
  }, [placeId, mapsLoaded]);

  let rangeResult = 0;
  if (userLocationCoordinates?.viewport) {
    rangeResult = computeCenterAndRadius(
      userLocationCoordinates?.viewport
    ).radius;
  }

  let handoutEnabled = false;
  if (
    placeId &&
    userLocationCoordinates.lat &&
    userLocationCoordinates.lng &&
    location.pathname === APP_ROUTES.HANDOUTS &&
    rangeResult
  ) {
    handoutEnabled = true;
  }

  const { data: groupData } = useActionGroupsHandouts({
    limit: 999,
    range: rangeResult,
    lat: userLocationCoordinates.lat,
    lng: userLocationCoordinates.lng,
    keepPreviousData: true,
    enabled: handoutEnabled,
    cacheTime: 0,
    staleTime: Infinity,
  });

  const prepareActionGroupData = useMemo(() => {
    return {
      action_group_info: groupData?.data.map((item) => ({
        title: [item.title],
        telegram_url: [item.address],
        description: [item.description],
        schedule: [item.schedule],
        contact: [item.contactUrl],
        stage: [item.stage],
        logo: [item.logo],
        issue_title: [item.significantIssueTitle],
        issue_location: [item.significantAddress],
        issue_rating: [getRating(item.significantIssueRating).toString()],
        issue_category: item.issueRatings,
      })),
      search_location: searchLocationName || "",
    };
  }, [groupData, searchLocationName]);

  const groupDataLength = groupData?.data.length;
  useEffect(() => {
    if (!setHandoutsNoGroupsFound) {
      return;
    }
    if (groupData?.data.length === 0) {
      setHandoutsNoGroupsFound("NoGroups");
    }
    if (groupData?.data && Number(groupDataLength) > 0) {
      setHandoutsNoGroupsFound("Groups");
    }
    if (!placeId) {
      setHandoutsNoGroupsFound("NoSearch");
    }
  }, [groupData?.data, groupDataLength, placeId, setHandoutsNoGroupsFound]);

  const { mutate: mutateHandoutSheet, data } = useCreateHandout({});
  useEffect(() => {
    if (data?.data[0]) {
      setPdfUrl?.(data?.data[0] as string);
    }
  }, [data, setPdfUrl]);

  const { data: categoriesData } = useIssueRatingCategories();

  const [scenarioSteps, setScenarioSteps] = useState<Step[]>(firstScenarioSteps);
  const [currentScenario, setCurrentScenario] = useState(1);

  const [{ run, stepIndex, steps }, setJoyrideState] = useSetState<State>({
    run: false,
    stepIndex: 0,
    steps: scenarioSteps,
  });

  const handleClickStart = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      window.scrollTo(0, 0);
      setJoyrideState((prevState) => ({
        ...prevState,
        run: true,
        stepIndex: 0,
      }));
    },
    [setJoyrideState]
  );

  const handleJoyrideCallback = useHandleJoyrideCallback({
    setJoyState: setJoyrideState,
    setPreventClose,
    setIsOpen,
    setOpenIssueModal,
    currentScenario,
  });

  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("tags") || "");

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const updateKeywordInUrl = useCallback(
    (value: string) => {
      const params = new URLSearchParams(searchParams);
      if (value) {
        params.set("tags", value);
      } else {
        params.delete("tags");
      }
      navigateRef.current({ search: params.toString() }, { replace: true });
    },
    [searchParams]
  );

  const debouncedKeywordUpdate = useDebouncedHandler(updateKeywordInUrl, 400);

  const handleKeywordChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      debouncedKeywordUpdate(value.trim());
    },
    [debouncedKeywordUpdate]
  );

  useEffect(() => {
    const tags = searchParams.getAll("tags");
    const nextValue = tags.length ? tags[0] : "";
    setSearchTerm((prev) => (prev === nextValue ? prev : nextValue));
  }, [searchParams]);

  const handleClearLocation = useCallback(() => {
    setSearchLocationName("");
    setUserLocationCoordinates(initialCoordinates);
    setQueryOptions((prev) => ({
      ...prev,
      placeId: "",
      lat: "",
      lng: "",
      zoom: "",
    }));
  }, [setQueryOptions]);

  const onLocationChange = useCallback(
    (value: any) => {
      const newPlaceId = value?.value as string;
      const label = value?.label || "";
      if (!newPlaceId) {
        handleClearLocation();
        return;
      }
      setSearchLocationName(label);
      setQueryOptions((prev) => ({
        ...prev,
        placeId: newPlaceId,
      }));
    },
    [handleClearLocation, setQueryOptions]
  );

  const handleCategoryToggle = useCallback(
    (categoryId: string) => {
      setQueryOptions((prev) => {
        const existing = Array.isArray(prev.category)
          ? prev.category
          : prev.category
          ? [prev.category]
          : [];
        const hasCategory = existing.includes(categoryId);
        return {
          ...prev,
          category: hasCategory
            ? existing.filter((item) => item !== categoryId)
            : [...existing, categoryId],
        };
      });
    },
    [setQueryOptions]
  );

  const handleToggleMine = useCallback(() => {
    if (!isAuth || !meData?.data.id) {
      setShowGoSignInModal(true);
      return;
    }
    setQueryOptions((prev) => ({
      ...prev,
      user: prev.user ? "" : meData?.data.id || "",
    }));
  }, [isAuth, meData?.data.id, setQueryOptions]);

  const handleApplyFilters = useCallback(() => {
    setFiltersOpen(false);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    updateKeywordInUrl("");
    setCurrentSelectedSkills([]);
    setSkillSearchParam("");
    handleClearLocation();
    setQueryOptions((prev) => ({
      ...prev,
      category: [],
      skills: [],
      user: "",
      placeId: "",
      lat: "",
      lng: "",
      zoom: "",
    }));
  }, [handleClearLocation, setQueryOptions, updateKeywordInUrl]);

  const handleReportClick = useCallback(() => {
    if (!isAuth) {
      setShowGoSignInModal(true);
      return;
    }
    setShowReportPromptModal(true);
  }, [isAuth]);

  const handleProposeClick = useCallback(() => {
    if (!isAuth) {
      setShowGoSignInModal(true);
      return;
    }
    setOpenWarningModal(true);
  }, [isAuth]);

  const handleCreateGroupClick = useCallback(() => {
    if (!isAuth) {
      setShowGoSignInModal(true);
      return;
    }
    setOpenWarningModal(true);
  }, [isAuth]);

  const handleFindResources = useCallback(() => {
    if (!groupData?.data?.length || !placeId) {
      return;
    }
    mutateHandoutSheet(prepareActionGroupData);
  }, [groupData?.data?.length, mutateHandoutSheet, placeId, prepareActionGroupData]);

  const handleLogoClick = useCallback(() => {
    navigate(APP_ROUTES.HOME);
  }, [navigate]);

  const handleHelpClick = useCallback(() => {
    if (!isAuth) {
      setShowWelcomeModal(true);
      return;
    }
    setOpenProductTourPopup((value) => !value);
  }, [isAuth]);

  const handleNotificationsClick = useCallback(() => {
    if (!isAuth) {
      setShowGoSignInModal(true);
      return;
    }
    if (dimensions.width <= 640) {
      setOpenNotificationSlideover(true);
      setOpenNotifications(false);
      return;
    }
    setOpenNotifications((value) => !value);
  }, [dimensions.width, isAuth]);

  const handleSignInClick = useCallback(() => {
    navigate(APP_ROUTES.SIGN_IN, { state: { from: location.pathname } });
  }, [navigate, location.pathname]);

  const handleSignUpClick = useCallback(() => {
    navigate(APP_ROUTES.SIGN_UP);
  }, [navigate]);

  const handleLogout = useCallback(() => {
    void logout();
  }, []);

  useEffect(() => {
    if (openNotificationSlideover === false) {
      setIsOpen(false);
    }
  }, [openNotificationSlideover]);

  useEffect(() => {
    if (createdIssueId) {
      setShowSuccessReportPromptModal(true);
    }
  }, [createdIssueId]);

  const queryClient = useQueryClient();

  const onCloseIssueReport = useCallback(() => {
    setCreatedIssueId(undefined);
    setOpenIssueModal(false);
    queryClient.getMutationCache().clear();
  }, [queryClient]);

  const onSuccessIssueReport = useCallback(
    (id?: string) => {
      setCreatedIssueId(id);
      setOpenIssueModal(false);
      queryClient.getMutationCache().clear();
    },
    [queryClient]
  );

  const notEnoughPersonalData =
    (!meData?.data.firstName && !meData?.data.lastName) ||
    !userSkillsData?.data ||
    userSkillsData?.data.length < 1;

  const amount = +(unreadActivitiesAmount?.data || 0);
  const notificationsCount =
    isNumber(amount) && !isNaN(amount || 0) ? amount : 0;

  const onSkillsChange = useCallback(
    (value: any) => {
      if (value && Array.isArray(value)) {
        setCurrentSelectedSkills(() => [...value]);
        setQueryOptions((prev) => ({
          ...prev,
          skills: [
            ...value.map((item) => {
              return item.value as string;
            }),
          ],
        }));
      }
      if (value && !Array.isArray(value)) {
        setCurrentSelectedSkills((prev) => [...prev, value]);
        setQueryOptions((prev) => {
          let skills: string[] = [];
          if (prev && prev?.skills && value?.value) {
            skills = [...(prev?.skills as string[]), value.value];
          }
          return {
            ...prev,
            skills,
          };
        });
      }
    },
    [setQueryOptions]
  );

  const [, setMutationCacheRerenderCounter] = useState(0);

  const subscribeCacheCallback = useCallback((event) => {
    if (!event) {
      return;
    }
    setMutationCacheRerenderCounter((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const unsubscribe = queryClient
      .getMutationCache()
      .subscribe(subscribeCacheCallback);
    return () => {
      unsubscribe();
    };
  }, [queryClient, subscribeCacheCallback]);

  const allMutations = queryClient
    .getMutationCache()
    .findAll({ mutationKey: ["createAIAnalyticsIssue"] }) as any[];

  const latestAIMutation = useMemo(
    () =>
      allMutations && allMutations.length > 0
        ? allMutations[allMutations.length - 1]?.state?.data?.data
        : allMutations[0]?.state?.data?.data,
    [allMutations]
  ) as IAISuggestions | undefined;

  const moreFeedbackQuality = useMemo(() => {
    if (!latestAIMutation) {
      return undefined;
    }
    return latestAIMutation.more_feedback;
  }, [latestAIMutation]);

  const pageContext: PageContextKey | null = useMemo(() => {
    if (location.pathname === APP_ROUTES.ISSUES) {
      return "issues";
    }
    if (location.pathname === APP_ROUTES.SOLUTION_LIST) {
      return "solutions";
    }
    if (location.pathname === APP_ROUTES.ACTION_GROUP_LIST) {
      return "action-groups";
    }
    if (location.pathname === APP_ROUTES.HANDOUTS) {
      return "handouts";
    }
    return null;
  }, [location.pathname]);

  const handleCtaByContext = useMemo(() => {
    return {
      issues: handleReportClick,
      solutions: handleProposeClick,
      "action-groups": handleCreateGroupClick,
      handouts: handleFindResources,
    } as Record<PageContextKey, () => void>;
  }, [handleCreateGroupClick, handleFindResources, handleProposeClick, handleReportClick]);

  const contextualConfig: ContextualConfig | null = useMemo(() => {
    if (!pageContext) {
      return null;
    }

    if (pageContext === "issues") {
      return {
        key: "issues",
        showSearch: false,
        showFilters: true,
        ctaLabel: "+ Report Issue",
        onCta: handleCtaByContext["issues"],
      };
    }

    if (pageContext === "solutions") {
      return {
        key: "solutions",
        showSearch: false,
        showFilters: true,
        ctaLabel: "+ Propose Solution",
        onCta: handleCtaByContext["solutions"],
      };
    }

    if (pageContext === "action-groups") {
      return {
        key: "action-groups",
        showSearch: false,
        showFilters: true,
        ctaLabel: "+ Create Action Group",
        onCta: handleCtaByContext["action-groups"],
      };
    }

    return {
      key: "handouts",
      showSearch: false,
      showFilters: true,
      ctaLabel: "Find resources",
      ctaDisabled: !groupData?.data?.length || !placeId,
      onCta: handleCtaByContext["handouts"],
    };
  }, [
    groupData?.data?.length,
    handleCtaByContext,
    pageContext,
    placeId,
  ]);

  const profileMenuItems = useMemo(
    () => [
      { name: "Dashboard", href: APP_ROUTES.DASHBOARD },
      { name: "My Profile", href: APP_ROUTES.PROFILE },
      { name: "Settings", href: APP_ROUTES.PROFILE },
      { name: "Logout", href: "#", action: "logout" as const },
    ],
    []
  );

  return (
    <div className="layout-width px-[5%] sm:px-0">
      <Joyride
        stepIndex={stepIndex}
        callback={handleJoyrideCallback}
        continuous
        hideBackButton
        disableScrolling
        hideCloseButton
        run={run}
        scrollToFirstStep
        showProgress
        showSkipButton
        steps={steps}
        styles={{
          options: {
            zIndex: 10000,
          },
        }}
      />

      <GlobalHeader
        primaryNav={primaryNavigation}
        moreNav={moreNavigation}
        profileMenu={profileMenuItems}
        isAuth={!!isAuth}
        profile={profile}
        isFetchingProfile={isFetching}
        avatarColor={avatarColor}
        notificationsCount={notificationsCount}
        highlightProfile={notEnoughPersonalData}
        onLogoClick={handleLogoClick}
        onHelpClick={handleHelpClick}
        helpContent={
          openProductTourPopup && (
            <ProductTourComponent
              setIsOpen={setIsOpen}
              setOpenNotificationSlideover={setOpenNotificationSlideover}
              setOpenProductTourPopup={setOpenProductTourPopup}
              setCurrentScenario={setCurrentScenario}
              setScenarioSteps={setScenarioSteps}
              handleClickStart={handleClickStart}
              fullProductTour={fullProductTour}
            />
          )
        }
        onNotificationsClick={handleNotificationsClick}
        notificationContent={
          openNotifications && (
            <NotificationPopup
              key="NotificationPopup"
              setOpenNotifications={setOpenNotifications}
            />
          )
        }
        onSignInClick={handleSignInClick}
        onSignUpClick={handleSignUpClick}
        onLogout={handleLogout}
        onMobileMenuToggle={() => setIsOpen((value) => !value)}
        isMobileMenuOpen={isOpen}
        showMobileMenuToggle
        isHelpActive={openProductTourPopup}
        isNotificationsActive={openNotifications}
      />

      {contextualConfig && (
        <ContextualBar
          searchValue={searchTerm}
          onSearchChange={handleKeywordChange}
          showSearch={contextualConfig.showSearch}
          showFilters={contextualConfig.showFilters}
          onFiltersClick={() => setFiltersOpen(true)}
          rightExtras={contextualConfig.rightExtras}
          cta={{
            label: contextualConfig.ctaLabel,
            onClick: contextualConfig.onCta,
            disabled: contextualConfig.ctaDisabled,
          }}
        />
      )}

      <FiltersDrawer
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onApply={handleApplyFilters}
        onClearAll={handleClearFilters}
        onLocationChange={onLocationChange}
        keywordValue={searchTerm}
        onKeywordChange={handleKeywordChange}
        page={(pageContext ?? 'issues') as PageContextKey}
        categories={categoriesData?.data}
        selectedCategories={values.category}
        onToggleCategory={handleCategoryToggle}
        skillOptions={skillOptions}
        selectedSkills={currentSelectedSkills}
        onSkillsChange={onSkillsChange}
        onSkillSearch={setSkillSearchParam}
        showMyToggle={!!isAuth && pageContext !== null && pageContext !== 'handouts'}
        myToggleLabel={
          pageContext === 'issues'
            ? 'Reported by Me'
            : pageContext === 'solutions'
            ? 'Proposed by Me'
            : pageContext === 'action-groups'
            ? 'Created by Me'
            : ''
        }
        myToggleEnabled={!!values.user}
        onToggleMine={handleToggleMine}
        selectedLocationName={searchLocationName}
        hasLocationFilter={!!placeId}
        onClearLocation={handleClearLocation}
      />

      <SlideMenuList
        isOpen={isOpen}
        preventClose={preventClose}
        setIsOpen={setIsOpen}
        isScrolled={false}
        isAuth={!!isAuth}
        profile={profile}
        notEnoughPersonalData={notEnoughPersonalData}
        setOpenNotificationSlideover={setOpenNotificationSlideover}
        setOpenProductTourPopup={setOpenProductTourPopup}
        openProductTourPopup={openProductTourPopup}
        setCurrentScenario={setCurrentScenario}
        setScenarioSteps={setScenarioSteps}
        handleClickStart={handleClickStart}
        setShowGoSignInModal={setShowGoSignInModal}
      />

      <SlideOver
        open={openNotificationSlideover}
        close={() => setOpenNotificationSlideover(false)}
        title="Notifications"
      >
        <div className="flex max-h-[calc(100vh-143px)]">
          <NotificationPopup
            mobileVersion
            setOpenNotifications={setOpenNotificationSlideover}
          />
        </div>
      </SlideOver>

      <SlideOver
        id="issue-report"
        open={openIssueModal}
        close={onCloseIssueReport}
        title="Report Issue"
        description={
          <div className="flex flex-col">
            <Dialog.Description className="mt-[14px] text-sm font-light text-gray-500">
              This information will be displayed publicly so be careful what you
              share.
            </Dialog.Description>
            {(latestAIMutation?.feedback_areas?.length || 0) > 0 ? (
              <Dialog.Description className="text-sm font-light text-red-500">
                Please review the highlighted sections for suggested
                improvements before we can save your post
              </Dialog.Description>
            ) : null}
            {moreFeedbackQuality &&
            (moreFeedbackQuality.quality === "Useless" ||
              moreFeedbackQuality.quality === "Poor") ? (
              <Dialog.Description className="text-sm font-light text-red-500">
                {latestAIMutation?.more_feedback?.feedback}
              </Dialog.Description>
            ) : null}
          </div>
        }
      >
        <IssueForm
          onSuccess={onSuccessIssueReport}
          onClose={onCloseIssueReport}
        />
      </SlideOver>

      {showSuccessReportPromptModal && createdIssueId && (
        <PromptModal
          setShow={setShowSuccessReportPromptModal}
          header="Report Issue"
          content="Will you like to propose a solution for this issue?"
          footer={
            <div className="flex w-full flex-row justify-between gap-2">
              <Button
                inGroupStyle={false}
                isStyleless
                isReverse
                className="shrink-0 border border-gray-500 text-black hover:border-black"
                onClick={() => {
                  setShowSuccessReportPromptModal(false);
                  navigate(APP_ROUTES.ISSUE + "/" + createdIssueId);
                }}
              >
                Yes
              </Button>
              <Button
                inGroupStyle={false}
                isStyleless
                isReverse
                className="border border-gray-500 text-black hover:border-black"
                onClick={() => {
                  setShowSuccessReportPromptModal(false);
                  setCreatedIssueId(undefined);
                }}
              >
                Maybe later
              </Button>
            </div>
          }
        />
      )}

      {openWarningModal && <WarningModal setShow={setOpenWarningModal} />}

      {showReportPromptModal && (
        <PromptModal
          setShow={setShowReportPromptModal}
          header="Report Issue"
          content="Before adding a new issue, please ensure a similar issue for this location does not exist already"
          footer={
            <div className="flex w-full flex-row justify-between gap-2">
              <Button
                inGroupStyle={false}
                isStyleless
                isReverse
                className="shrink-0 border border-gray-500 text-black hover:border-black"
                onClick={() => {
                  setShowReportPromptModal(false);
                  setOpenIssueModal(true);
                }}
              >
                Continue, I have checked
              </Button>
              <Button
                inGroupStyle={false}
                isStyleless
                isReverse
                className="border border-gray-500 text-black hover:border-black"
                onClick={() => {
                  setShowReportPromptModal(false);
                }}
              >
                Cancel
              </Button>
            </div>
          }
        />
      )}

      {showGoSignInModal && (
        <PromptModal
          setShow={setShowGoSignInModal}
          header="Warning"
          content="You have to sign in to perform this action"
          footer={
            <div className="flex w-full flex-row justify-between gap-2">
              <Button
                inGroupStyle={false}
                isStyleless
                isReverse
                className="shrink-0 border border-gray-500 text-black hover:border-black"
                onClick={() => {
                  setShowGoSignInModal(false);
                  setTimeout(() => {
                    navigate(APP_ROUTES.SIGN_IN);
                  }, 100);
                }}
              >
                Ok
              </Button>
              <Button
                inGroupStyle={false}
                isStyleless
                isReverse
                className="border border-gray-500 text-black hover:border-black"
                onClick={(event) => {
                  event.stopPropagation();
                  setShowGoSignInModal(false);
                }}
              >
                Maybe later
              </Button>
            </div>
          }
        />
      )}

      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
      />
    </div>
  );
}

export default memo(DesktopMenu);
